import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { setCurrentStep } from "../../checkoutSlice";
import { RootState } from "../../../../../app/store";
import { useUpdateCustomer } from "../../../cart/hooks/useUpdateCustomer";
import { useGetCheckoutDataQuery } from "../../api/checkoutApi";
import { useGetCartQuery } from "../../../cart/api/cartApi";
import { TransformedAddress } from "../../../cart/types/cartApiTypes";
import {
  createBillingSchema,
  BillingFormData,
} from "../../helpers/checkoutValidation";
import { useUnsavedChangesWarning } from "../../../../../common/hooks/useUnsavedChangesWarning";
import CheckoutDetailsForm from "../common/CheckoutDetailsForm/CheckoutDetailsForm";
import CheckoutStepLayout from "../common/CheckoutStepLayout";
import UseBillingForShippingToggle from "../common/UseBillingForShippingToggle";

/**
 * Billing Step Component
 */
const BillingStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // --- Data Fetching ---
  const { data: checkoutData } = useGetCheckoutDataQuery();
  const { data: cartData } = useGetCartQuery();

  const billingAddress =
    checkoutData?.billingAddress || ({} as TransformedAddress);

  // --- Global State ---
  const useBillingForShipping = useAppSelector(
    (state: RootState) => state.checkout.useBillingForShipping
  );

  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  // --- Form Initialization ---
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm<BillingFormData>({
    resolver: zodResolver(createBillingSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      ...billingAddress,
      customerType: billingAddress.customerType || "individual",
      tckn: billingAddress.tckn || "",
      vkn: billingAddress.vkn || "",
      company: billingAddress.company || "",
      // Use existing country or default to empty to force selection
      country: billingAddress.country || "",
    },
  });

  useUnsavedChangesWarning(isDirty, isSubmitting);

  // --- Server State Sync ---
  useEffect(() => {
    if (checkoutData?.billingAddress) {
      const ba = checkoutData.billingAddress;
      if (!isDirty) {
        reset({
          ...ba,
          customerType: ba.customerType || "individual",
          tckn: ba.tckn || "",
          vkn: ba.vkn || "",
          company: ba.company || "",
          country: ba.country || "",
        });
      }
    }
  }, [checkoutData, reset, isDirty]);

  // --- Submission Handler ---
  const onSubmit = async (data: BillingFormData) => {
    // 1. Clean Contextual Data
    const isTurkey = data.country === "TR";

    // Only send TCKN/VKN if country is Turkey. Otherwise send empty string.
    const cleanTckn =
      isTurkey && data.customerType === "individual" ? data.tckn : "";
    const cleanVkn =
      isTurkey && data.customerType === "corporate" ? data.vkn : "";

    // Only send Company if Corporate (Global rule)
    const cleanCompany = data.customerType === "corporate" ? data.company : "";

    // 2. Prepare Standard Address Payload
    const finalData = {
      first_name: data.firstName,
      last_name: data.lastName,
      company: cleanCompany,
      country: data.country,
      address_1: data.address1,
      address_2: data.address2,
      city: data.city,
      state: data.state,
      postcode: data.postcode,
      phone: data.phone,
      email: data.email,
    };

    // 3. Construct Full API Payload
    const payload: any = {
      billing_address: finalData,
      extensions: {
        additional_billing_info: {
          customer_type: data.customerType,
          tckn: cleanTckn,
          vkn: cleanVkn,
        },
      },
    };

    // 4. Handle "Use Billing for Shipping" Logic
    if (useBillingForShipping) {
      payload.shipping_address = finalData;
    }

    // 5. Execute Server Update
    const result = await handleUpdateCustomer(payload);

    // 6. Navigate on Success
    if (result.success) {
      reset(data); // Mark as clean

      if (cartData?.needsShipping) {
        dispatch(setCurrentStep("shipping"));
      } else {
        dispatch(setCurrentStep("payment"));
      }
    }
  };

  const onError = () => {
    toast.error(
      t(
        "feedback.cart.customer.missingFieldBackend",
        "Please fill in all required fields."
      )
    );
  };

  return (
    <CheckoutStepLayout
      title={t("billingStep.stepTitle", "Billing Address")}
      onSubmit={handleSubmit(onSubmit, onError)}
      isProcessing={isUpdatingCustomer}
      nextButtonText={t("checkoutForm.actions.continue", "Continue")}
      isNextDisabled={false}
      afterFields={<UseBillingForShippingToggle />}
      // Configuration: Only Next button, it submits.
      showPrevButton={false}
      showNextButton={true}
      nextButtonType="submit"
    >
      <CheckoutDetailsForm
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        watch={watch}
      />
    </CheckoutStepLayout>
  );
};

export default BillingStep;
